import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import { useInitialTailgateGrid } from './useInitialTailgateGrid'
import { useSetupBox } from './useSetupBox'

import { ScaleLinear } from 'd3'

import { AssociateDeviceI } from '@/features/floor-plan'
import { VideoDimensionsIProps, useVideoFrame } from '@/features/video-player'

import { TailgateBox, TailgateGridClick } from '../types/types'
import {
  EMPTY_BOX,
  IMAGE_HEIGHT,
  IMAGE_WIDTH,
  areBoxesEqual,
  calculateAspectRatioFit,
  copyBox,
  getInitialTailgateGrid,
  getScale,
} from '../utils/utils'

type TailgateGridContextType = {
  DummyPlayer: () => JSX.Element
  associateDeviceValues: AssociateDeviceI | null
  onSaveCallback?: () => void
  clearBoxes: () => void
  videoDimensions?: VideoDimensionsIProps
  handleOnClick: (e: TailgateGridClick) => void
  isImageLoading: boolean
  imageUrl: string
  inBox: TailgateBox
  isInitialGridLoading: boolean
  isDirty: boolean
  isGridConfigured: boolean
  isTailgateGridOpen: boolean
  onTailgateGridClose: () => void
  outBox: TailgateBox
  refreshSnapshot: () => Promise<void>
  resetVideoFrame: () => void
  isRefreshingImage: boolean
  scaleX: ScaleLinear<number, number, never>
  scaleY: ScaleLinear<number, number, never>
  setInBox: Dispatch<SetStateAction<TailgateBox>>
  setInitialBoxes: () => void
  setOutBox: Dispatch<SetStateAction<TailgateBox>>
  trackTailgateEvent: (name: string) => void
  shouldRenderDummyPlayer: boolean
}

export const TailgateGridContext = createContext<TailgateGridContextType>(null)

const FIRST_CAPTURE_COUNT = 1

interface TailgateGridProviderIProps {
  children: ReactNode
  associateDeviceValues: AssociateDeviceI | null
  trackTailgateEvent: (name: string) => void
  isGridConfigured?: boolean
  isTailgateGridOpen?: boolean
  onTailgateGridClose: () => void
  onSaveCallback?: () => void
  shouldRenderDummyPlayer?: boolean
}

export const TailgateGridProvider = ({
  children,
  associateDeviceValues,
  trackTailgateEvent,
  isGridConfigured,
  isTailgateGridOpen,
  shouldRenderDummyPlayer,
  onTailgateGridClose,
  onSaveCallback,
}: TailgateGridProviderIProps) => {
  const {
    imageUrl,
    isLoading: isImageLoading,
    DummyPlayer,
    refreshSnapshot,
    resetVideoFrame,
    isRefreshingImage,
    videoDimensions,
    captureCount,
  } = useVideoFrame({
    cameraId: associateDeviceValues?.cameraId,
    isStreamActive: Boolean(isTailgateGridOpen),
  })

  const { width, height } = calculateAspectRatioFit(
    videoDimensions?.width,
    videoDimensions?.height,
    IMAGE_WIDTH,
    IMAGE_HEIGHT
  )
  const scaleX = getScale(width, videoDimensions?.width)
  const scaleY = getScale(height, videoDimensions?.height)

  const { outBox, setOutBox, inBox, setInBox, handleOnClick } = useSetupBox({
    scaleX,
    scaleY,
  })

  const { isInitialGridLoading, initialTailgateGrid } = useInitialTailgateGrid({
    cameraId: associateDeviceValues?.cameraId,
    doorId: associateDeviceValues?.doorId,
    isGridConfigured,
  })

  const { initialInBox, initialOutBox } = useMemo(
    () =>
      getInitialTailgateGrid(videoDimensions, initialTailgateGrid, {
        width,
        height,
      }),
    [videoDimensions, initialTailgateGrid]
  )

  // First frame load (first modal open)
  useEffect(() => {
    if (isTailgateGridOpen && captureCount === FIRST_CAPTURE_COUNT) {
      setInitialBoxes()
    }
  }, [imageUrl])

  // Subsequent modal opening (frame already loaded)
  useEffect(() => {
    if (isTailgateGridOpen) {
      setInitialBoxes()
    }
  }, [isTailgateGridOpen])

  const setInitialBoxes = () => {
    if (initialTailgateGrid && imageUrl) {
      // Boxes need to be copied because of referential equality
      // Without copy setInBox/setOutBox modifies coordinates of initialInBox/initialOutBox
      setOutBox(copyBox(initialOutBox))
      setInBox(copyBox(initialInBox))
    }
  }

  const clearBoxes = () => {
    setOutBox(EMPTY_BOX)
    setInBox(EMPTY_BOX)
  }

  const isDirty =
    !areBoxesEqual(inBox, initialInBox) || !areBoxesEqual(outBox, initialOutBox)

  return (
    <TailgateGridContext.Provider
      value={{
        DummyPlayer,
        associateDeviceValues,
        onSaveCallback,
        clearBoxes,
        videoDimensions,
        handleOnClick,
        isImageLoading,
        imageUrl,
        inBox,
        isInitialGridLoading,
        isDirty,
        isGridConfigured,
        isTailgateGridOpen,
        onTailgateGridClose,
        outBox,
        refreshSnapshot,
        resetVideoFrame,
        isRefreshingImage,
        scaleX,
        scaleY,
        setInBox,
        setInitialBoxes,
        setOutBox,
        trackTailgateEvent,
        shouldRenderDummyPlayer,
      }}
    >
      {children}
    </TailgateGridContext.Provider>
  )
}
export const useTailgateGrid = () => {
  return useContext(TailgateGridContext)
}
